// 권위: Type-A BootstrapProposal v2.0 `feedback[]` 채널 + ARCH_DASHBOARD.md §4.11 피드백 탭 스펙.
//
// 피드백 탭 전용 UI 타입: 필터 상태 · URL 쿼리 직렬화 · 딥링크 라우팅 빌더.
// API DTO 는 src/api/sectorTaxonomyApi.ts 권위.

import type {
  TaxonomyFeedbackKind,
  TaxonomyFeedbackScope,
  TaxonomyFeedbackSeverity,
  FeedbackListQuery,
} from '../../api/sectorTaxonomyApi';
import { shiftIsoDate, todayIsoDate } from './sectorTaxonomy.types';

export const ALL_FEEDBACK_KINDS: TaxonomyFeedbackKind[] = [
  'question',
  'improvement_request',
  'data_issue',
  'ambiguity_flag',
];

export const ALL_FEEDBACK_SEVERITIES: TaxonomyFeedbackSeverity[] = [
  'info',
  'suggestion',
  'blocker',
];

export const ALL_FEEDBACK_SCOPES: TaxonomyFeedbackScope[] = [
  'input_data',
  'prompt',
  'output_schema',
  'specific_sector',
];

export const FEEDBACK_PAGE_SIZES: number[] = [25, 50, 100];

export function formatFeedbackKind(k: string): string {
  switch (k) {
    case 'question':
      return '질문';
    case 'improvement_request':
      return '개선 요청';
    case 'data_issue':
      return '데이터 이슈';
    case 'ambiguity_flag':
      return '모호성';
    default:
      return k;
  }
}

export function formatFeedbackSeverity(s: string): string {
  switch (s) {
    case 'info':
      return 'info';
    case 'suggestion':
      return 'suggestion';
    case 'blocker':
      return 'blocker';
    default:
      return s;
  }
}

export function formatFeedbackScope(s: string): string {
  switch (s) {
    case 'input_data':
      return '입력 데이터';
    case 'prompt':
      return '프롬프트';
    case 'output_schema':
      return '출력 스키마';
    case 'specific_sector':
      return '특정 섹터';
    default:
      return s;
  }
}

// ── 필터 상태 ──────────────────────────────────────────────────
export interface FeedbackFilters {
  from: string;                                  // "YYYY-MM-DD"
  to: string;                                    // "YYYY-MM-DD"
  runId: string;                                 // "" = 전체
  kind: TaxonomyFeedbackKind[];
  severity: TaxonomyFeedbackSeverity[];
  scope: TaxonomyFeedbackScope[];
  blockerOnly: boolean;
  page: number;                                  // 0-origin
  pageSize: number;
}

export function buildDefaultFeedbackFilters(): FeedbackFilters {
  return {
    from: shiftIsoDate(30),
    to: todayIsoDate(),
    runId: '',
    kind: [],
    severity: [],
    scope: [],
    blockerOnly: false,
    page: 0,
    pageSize: 50,
  };
}

export function feedbackFiltersToSearchParams(f: FeedbackFilters): URLSearchParams {
  const sp = new URLSearchParams();
  const def = buildDefaultFeedbackFilters();
  if (f.from && f.from !== def.from) sp.set('from', f.from);
  if (f.to && f.to !== def.to) sp.set('to', f.to);
  if (f.runId) sp.set('run_id', f.runId);
  if (f.kind.length > 0) sp.set('kind', f.kind.join(','));
  if (f.severity.length > 0) sp.set('severity', f.severity.join(','));
  if (f.scope.length > 0) sp.set('scope', f.scope.join(','));
  if (f.blockerOnly) sp.set('blocker_only', 'true');
  if (f.page > 0) sp.set('page', String(f.page));
  if (f.pageSize !== def.pageSize) sp.set('page_size', String(f.pageSize));
  return sp;
}

export function feedbackFiltersFromSearchParams(sp: URLSearchParams): FeedbackFilters {
  const def = buildDefaultFeedbackFilters();
  const kindRaw = (sp.get('kind') ?? '').split(',').map((s) => s.trim()).filter(Boolean);
  const kind = kindRaw.filter((k): k is TaxonomyFeedbackKind =>
    (ALL_FEEDBACK_KINDS as string[]).includes(k)
  );
  const sevRaw = (sp.get('severity') ?? '').split(',').map((s) => s.trim()).filter(Boolean);
  const severity = sevRaw.filter((k): k is TaxonomyFeedbackSeverity =>
    (ALL_FEEDBACK_SEVERITIES as string[]).includes(k)
  );
  const scopeRaw = (sp.get('scope') ?? '').split(',').map((s) => s.trim()).filter(Boolean);
  const scope = scopeRaw.filter((k): k is TaxonomyFeedbackScope =>
    (ALL_FEEDBACK_SCOPES as string[]).includes(k)
  );
  const page = parseInt(sp.get('page') ?? '', 10);
  const pageSize = parseInt(sp.get('page_size') ?? '', 10);
  return {
    from: sp.get('from') ?? def.from,
    to: sp.get('to') ?? def.to,
    runId: (sp.get('run_id') ?? '').trim(),
    kind,
    severity,
    scope,
    blockerOnly: (sp.get('blocker_only') ?? '').toLowerCase() === 'true',
    page: Number.isFinite(page) && page > 0 ? page : 0,
    pageSize: FEEDBACK_PAGE_SIZES.includes(pageSize) ? pageSize : def.pageSize,
  };
}

/** 필터 → API 쿼리 객체 (listFeedback 호출용). blocker_only 토글은 severity=['blocker'] 로 치환. */
export function feedbackFiltersToQuery(f: FeedbackFilters): FeedbackListQuery {
  const severity = f.blockerOnly
    ? (['blocker'] as TaxonomyFeedbackSeverity[])
    : f.severity.length > 0
      ? f.severity
      : undefined;
  return {
    from: f.from || undefined,
    to: f.to || undefined,
    runId: f.runId || undefined,
    kind: f.kind.length > 0 ? f.kind : undefined,
    severity,
    scope: f.scope.length > 0 ? f.scope : undefined,
    page: Math.max(0, f.page),
    pageSize: Math.max(1, Math.min(200, f.pageSize)),
  };
}

// ── 딥링크 라우팅 ───────────────────────────────────────────────
// FeedbackTab 행의 targetRef 를 택소노미/매핑 탭 URL 로 변환한다.
// 스펙:
//   - "sec_xxx"                → /sectors/taxonomy?tab=tree&highlight=sec_xxx
//   - "naver_theme:반도체장비"  → /sectors/taxonomy?tab=mappings&source=naver_theme&sourceName=반도체장비
//   - "naver_primary:ABC"       → /sectors/taxonomy?tab=mappings&source=naver_primary&sourceName=ABC
//   - 그 외 / null              → null (코드 블록으로 렌더)

export interface TargetRefLink {
  path: string;
  tab: string;
  query: Record<string, string>;
}

export function buildTargetRefLink(ref: string | null): TargetRefLink | null {
  if (!ref) return null;
  const trimmed = ref.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('sec_')) {
    return {
      path: '/sectors/taxonomy',
      tab: 'tree',
      query: { highlight: trimmed },
    };
  }
  const m = trimmed.match(/^(naver_primary|naver_theme):(.+)$/);
  if (m) {
    return {
      path: '/sectors/taxonomy',
      tab: 'mappings',
      query: { source: m[1], sourceName: m[2] },
    };
  }
  return null;
}

/** TargetRefLink → react-router path+search string. */
export function targetRefLinkToHref(link: TargetRefLink): string {
  const sp = new URLSearchParams();
  sp.set('tab', link.tab);
  for (const [k, v] of Object.entries(link.query)) {
    sp.set(k, v);
  }
  return `${link.path}?${sp.toString()}`;
}
